import { useEffect, useState } from 'react';
import { Download, ExternalLink, Loader2, X } from 'lucide-react';
import { api } from '@/lib/api';
import type { DocumentoCandidato } from '@/lib/candidatos';

/**
 * Ver un currículum sin salir de la ficha.
 *
 * Los PDF van en un `<iframe>`, como el visor de contratos; las fotos, en un
 * `<img>`. Safari de iPhone no pagina bien los PDF dentro de un iframe —enseña
 * la primera página y no deja pasar—, así que en pantallas estrechas se ofrece
 * abrirlo en una pestaña, que es donde el sistema usa su propio visor.
 */
interface Props {
  candidatoId: string;
  documento: DocumentoCandidato;
  onClose: () => void;
}

function esImagen(doc: DocumentoCandidato): boolean {
  return (doc.contentType || '').startsWith('image/') || /\.(jpe?g|png|webp)$/i.test(doc.fileName);
}

function esPdf(doc: DocumentoCandidato): boolean {
  return doc.contentType === 'application/pdf' || /\.pdf$/i.test(doc.fileName);
}

export default function VisorDocumento({ candidatoId, documento, onClose }: Props) {
  const [cargando, setCargando] = useState(true);
  const url = api.urlDocumentoCandidato(candidatoId, documento.id);
  const urlDescarga = api.urlDocumentoCandidato(candidatoId, documento.id, true);
  const visualizable = esImagen(documento) || esPdf(documento);

  // Escape cierra, y el fondo no se desplaza detrás del visor.
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', alPulsar);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', alPulsar);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-label={documento.fileName}>
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />

      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-5xl h-[92vh] sm:h-[90vh] mx-2 sm:mx-4 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-2 p-3 sm:p-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-sm sm:text-base font-semibold truncate">{documento.fileName}</h2>
          <div className="flex items-center gap-1 flex-shrink-0">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost h-10 flex items-center gap-1.5 text-sm"
              title="Abrir en una pestaña nueva"
            >
              <ExternalLink size={16} />
              <span className="hidden sm:inline">Abrir</span>
            </a>
            <a
              href={urlDescarga}
              className="btn-ghost h-10 flex items-center gap-1.5 text-sm"
              title="Descargar"
            >
              <Download size={16} />
              <span className="hidden sm:inline">Descargar</span>
            </a>
            <button
              onClick={onClose}
              className="h-10 w-10 rounded-lg hover:bg-gray-100 flex items-center justify-center"
              aria-label="Cerrar"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 bg-slate-50 relative">
          {cargando && visualizable && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 size={24} className="animate-spin text-sereno-dark/40" />
            </div>
          )}

          {esImagen(documento) && (
            <img
              src={url}
              alt={documento.fileName}
              onLoad={() => setCargando(false)}
              className="w-full h-full object-contain"
            />
          )}

          {esPdf(documento) && (
            <iframe
              src={url}
              title={documento.fileName}
              onLoad={() => setCargando(false)}
              className="w-full h-full border-0"
            />
          )}

          {!visualizable && (
            <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
              <p className="text-sm text-sereno-dark/60">
                Este tipo de archivo no se puede ver aquí dentro.
              </p>
              <a href={urlDescarga} className="btn-primary text-sm flex items-center gap-2">
                <Download size={16} /> Descargar
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
